import React, { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  RiFullscreenFill,
  RiPauseFill,
  RiPlayFill,
  RiVolumeDownFill,
  RiVolumeMuteFill,
} from "@remixicon/react";
import { VideoRetentionData } from "@/screens/creatives/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const VolumeIcon = ({ muted }: { muted: boolean }) => (
  <>
    {muted ? (
      <RiVolumeMuteFill size={18} className="text-white shrink-0" />
    ) : (
      <RiVolumeDownFill size={18} className="text-white shrink-0" />
    )}
  </>
);

// Format time in mm:ss
const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Get retention value at a specific percentage of video progress
const getRetentionAtPercentage = (
  data: VideoRetentionData | undefined,
  percentage: number,
): number | null => {
  if (!data || data.length === 0) return null;

  // Sort data by percentage
  const sorted = [...data].sort((a, b) => a.percentage - b.percentage);

  // Find the two data points surrounding the target percentage
  let lower = { percentage: 0, retention: 100 };
  let upper = sorted[sorted.length - 1];

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].percentage <= percentage) {
      lower = sorted[i];
    }
    if (sorted[i].percentage >= percentage) {
      upper = sorted[i];
      break;
    }
  }

  // If exact match, return directly
  if (lower.percentage === percentage) return lower.retention;
  if (upper.percentage === percentage) return upper.retention;

  // Linear interpolation between two points
  if (lower.percentage === upper.percentage) return lower.retention;

  const ratio = (percentage - lower.percentage) / (upper.percentage - lower.percentage);
  return lower.retention + ratio * (upper.retention - lower.retention);
};

export interface VideoControlsProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  onPlayPause: () => void;
  isMuted: boolean;
  onMuteToggle: () => void;
  showControls: boolean;
  isHovered: boolean;
  currentTime: number;
  duration: number;
  videoRetentionData?: VideoRetentionData;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  videoRef,
  onPlayPause,
  isMuted,
  onMuteToggle,
  showControls,
  isHovered,
  currentTime,
  duration,
  videoRetentionData,
}) => {
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekTime, setSeekTime] = useState(0);
  const seekBarRef = useRef<HTMLDivElement>(null);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [isVolumeDragging, setIsVolumeDragging] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const lastSeekTimeRef = useRef<number | null>(null);

  // Sync seekTime with currentTime when they're close (within 0.1s tolerance)
  // This prevents flickering when seeking while paused
  useEffect(() => {
    if (!isSeeking && lastSeekTimeRef.current !== null) {
      // Check if currentTime has caught up to the seek time
      const timeDiff = Math.abs(currentTime - lastSeekTimeRef.current);
      if (timeDiff < 0.1) {
        // Current time has caught up, clear the ref
        lastSeekTimeRef.current = null;
      }
    }
  }, [currentTime, isSeeking]);

  // Use seekTime while seeking or until currentTime catches up after seeking
  const displayTime =
    isSeeking || lastSeekTimeRef.current !== null ? seekTime : currentTime;

  // Initialize volume from video element
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      setVolume(video.volume);
    }
  }, [videoRef]);

  // Sync volume with video element
  useEffect(() => {
    const video = videoRef.current;
    if (video && !isVolumeDragging) {
      setVolume(video.volume);
    }
  }, [videoRef, isVolumeDragging]);

  // Auto-hide controls based on hover state
  useEffect(() => {
    if (!showControls) {
      setIsControlsVisible(false);
      return;
    }

    // Show controls when hovered or video is paused
    if (isHovered || videoRef.current?.paused) {
      setIsControlsVisible(true);
    } else {
      // Hide controls after a delay when not hovered and video is playing

      if (!videoRef.current?.paused) {
        setIsControlsVisible(false);
      }
    }
  }, [showControls, isHovered, videoRef]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
      const video = videoRef.current;
      const seekBar = seekBarRef.current;
      if (!video || !seekBar) return;

      const rect = seekBar.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const newTime = percent * duration;
      video.currentTime = newTime;
      setSeekTime(newTime);
      lastSeekTimeRef.current = newTime;
    },
    [videoRef, duration],
  );

  const handleSeekStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    // Store whether video was playing before seeking
    const wasPlaying = !video.paused;

    // Pause video while seeking for smoother experience
    if (wasPlaying) {
      video.pause();
    }

    setIsSeeking(true);
    handleSeek(e);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      handleSeek(moveEvent);
    };

    const handleMouseUp = () => {
      setIsSeeking(false);
      // Resume playing if video was playing before seeking
      if (wasPlaying && video) {
        void video.play();
      }
      // Keep lastSeekTimeRef set so displayTime continues using seekTime
      // until currentTime prop catches up (handled in useEffect)
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void video.requestFullscreen();
    }
  };

  const handleVolumeChange = useCallback(
    (clientX: number) => {
      const video = videoRef.current;
      const volumeSlider = volumeSliderRef.current;
      if (!video || !volumeSlider) return;

      const rect = volumeSlider.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newVolume = percent;
      video.volume = newVolume;
      setVolume(newVolume);

      // Unmute if volume is set above 0
      if (newVolume > 0 && isMuted) {
        video.muted = false;
        onMuteToggle();
      }
      // Mute if volume is set to 0
      if (newVolume === 0 && !isMuted) {
        video.muted = true;
        onMuteToggle();
      }
    },
    [videoRef, isMuted, onMuteToggle],
  );

  // Handle mouse move and mouse up for volume dragging
  useEffect(() => {
    if (!isVolumeDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleVolumeChange(e.clientX);
    };

    const handleMouseUp = () => {
      setIsVolumeDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isVolumeDragging, handleVolumeChange]);

  const handleVolumeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsVolumeDragging(true);
    setIsVolumeHovered(true);
    handleVolumeChange(e.clientX);
  };

  const progress = duration > 0 ? (displayTime / duration) * 100 : 0;

  if (!showControls) return null;

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 right-0 z-20 transition-opacity duration-500",
        isControlsVisible ? "opacity-100" : "opacity-0",
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Gradient overlay for better visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />

      {/* Seek bar - positioned above other controls */}
      <div className="relative px-3 pt-4 pb-0">
        {/* Increased clickable area with padding */}
        <div
          ref={seekBarRef}
          className="relative py-2 cursor-pointer group"
          onMouseDown={handleSeekStart}
        >
          {/* Visual slider track - centered in the clickable area */}
          <div className="relative h-1 bg-white/30 rounded-full">
            {/* Progress track */}
            <div
              className="absolute top-0 left-0 h-full bg-white rounded-full"
              style={{ width: `${progress}%` }}
            />
            {/* Seek thumb with tooltip */}
            {videoRetentionData && videoRetentionData.length > 0 ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip open={isSeeking ? true : isTooltipOpen}>
                  <TooltipTrigger
                    asChild
                    onMouseEnter={() => setIsTooltipOpen(true)}
                    // If the element is focused, which would have required click or keyboard navigation
                    // don't hide the tooltip on mouse leave
                    onMouseLeave={(e) =>
                      e.target !== document.activeElement && setIsTooltipOpen(false)
                    }
                    onFocus={() => setIsTooltipOpen(true)}
                    onBlur={() => setIsTooltipOpen(false)}
                  >
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      style={{ left: `${progress}%`, transform: "translate(-50%, -50%)" }}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={8} align="start">
                    <div className="text-white text-[12px] text-nowrap">
                      Audience retention:{" "}
                      <span className="font-medium">
                        {getRetentionAtPercentage(videoRetentionData, progress)?.toFixed(
                          1,
                        )}
                        %
                      </span>
                    </div>
                    <div className="text-white text-[12px] text-nowrap">
                      Timestamp:{" "}
                      <span className="font-medium">{formatTime(displayTime)}</span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                style={{ left: `${progress}%`, transform: "translate(-50%, -50%)" }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Control buttons */}
      <div className="relative h-[20px] flex items-center justify-between gap-2 mx-[12px] mb-[12px] mt-[8px]">
        <div className="flex items-center gap-1">
          {/* Play/Pause button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            {videoRef.current?.paused ? (
              <RiPlayFill size={18} className="text-white shrink-0" />
            ) : (
              <RiPauseFill size={18} className="text-white shrink-0" />
            )}
          </button>

          {/* Time display */}
          <span className="mt-[2px] text-white text-xs font-medium tabular-nums text-nowrap">
            {formatTime(displayTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-1 justify-end">
          {/* Volume control with slider */}
          <div
            className="relative flex-1 flex justify-end items-center"
            onMouseEnter={() => setIsVolumeHovered(true)}
            onMouseLeave={() => {
              if (!isVolumeDragging) {
                setIsVolumeHovered(false);
              }
            }}
          >
            {/* Volume slider - appears on hover */}
            {isVolumeHovered && (
              <div
                ref={volumeSliderRef}
                className="relative w-full max-w-20 py-2 cursor-pointer mr-1"
                onMouseDown={handleVolumeStart}
              >
                {/* Visual slider track - centered in the clickable area */}
                <div className="relative h-1 bg-white/30 rounded-full">
                  {/* Volume track */}
                  <div
                    className="absolute top-0 left-0 h-full bg-white rounded-full"
                    style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                  />
                  {/* Volume thumb */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md cursor-pointer"
                    style={{
                      left: `${(isMuted ? 0 : volume) * 100}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </div>
              </div>
            )}
            {/* Volume button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMuteToggle();
              }}
              className="p-1 hover:bg-white/10 rounded-full transition-colors"
            >
              <VolumeIcon muted={isMuted} />
            </button>
          </div>

          {/* Fullscreen button */}
          <button
            onClick={handleFullscreen}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <RiFullscreenFill size={18} className="text-white shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoControls;
